import React, { useState, useEffect, useCallback, useContext } from "react";
import RoomDetailsCard from "./Room-Details-Card";
import { IoMdArrowRoundBack, IoMdArrowRoundForward } from "react-icons/io";

//import context
import { MyContext } from "../../../../tenantsDashContext";

//import resize function
import { vw, vh } from "../../../assets/reusableComponents/resizeFunction";

const styles = {
  carouselContainer: {
    display: "flex",
    width: "55.5vw",
    overflowX: "hidden",
    minHeight: vh(308),
    flexWrap: "nowrap",
    overflow: "hidden",
  },
  innerContainer: {
    display: "flex",
    width: "fit-content",
    gap: vw(50),
    flexWrap: "nowrap",
    padding: "0px",
    transition: "transform 0.3s ease-in-out",
  },
  prevButton: {
    position: "absolute",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: "2",
    top: "50%",
    left: "-3%",
    border: "none",
    backgroundColor: "#ffffff",
    borderRadius: "50%",
    width: vw(40),
    height: vw(40),
    boxShadow: "1px 1px 6px rgba(0, 0, 0, 0.1)",
    transition: "transform 0.3s ease-in-out",
  },
  buttonHover: {
    transform: "scale(1.05)",
  },
  nextButton: {
    position: "absolute",
    top: "50%",
    right: "-3%",
    zIndex: "2",
    border: "none",
    backgroundColor: "#ffffff",
    borderRadius: "50%",
    width: vw(40),
    height: vw(40),
    boxShadow: "1px 1px 6px rgba(0, 0, 0, 0.1)",
    transition: "transform 0.3s ease-in-out",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
  },
};

const Carousel = ({ propertiesPassed }) => {
  const { currentProperty, setCurrentProperty } = useContext(MyContext);

  const CARD_WIDTH = (370 / 1440) * 100;

  // width + gap
  const ITEMS_COUNT = 7;
  const [currentIndex, setCurrentIndex] = useState(1);
  const [isHovered, setIsHovered] = useState({ prev: false, next: false });
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isStopped, setIsStopped] = useState(false); // NEW: controls auto-scroll

  // Handler to set property and stop animation
  const handleCardClick = (property, index) => {
    setCurrentProperty(property);
    setIsStopped(true);
    setCurrentIndex(index + 1); // +1 because of the cloned first element
  };

  const items = [
    <RoomDetailsCard
      key="clone-last"
      {...propertiesPassed[propertiesPassed.length - 1]}
      propertyDetails={propertiesPassed[propertiesPassed.length - 1]}
      onCardClick={() =>
        handleCardClick(
          propertiesPassed[propertiesPassed.length - 1],
          propertiesPassed.length - 1
        )
      }
    />,
    ...propertiesPassed.map((property, index) => (
      <RoomDetailsCard
        key={index}
        propertyDetails={property}
        onCardClick={() => handleCardClick(property, index)}
      />
    )),
    ...(propertiesPassed.length > 3
      ? [
          <RoomDetailsCard
            key="clone-first"
            {...propertiesPassed[0]}
            propertyDetails={propertiesPassed[0]}
            onCardClick={() => handleCardClick(propertiesPassed[0], 0)}
          />,
        ]
      : []),
  ];

  const handleTransitionEnd = useCallback(() => {
    setIsTransitioning(false);
    if (currentIndex === 0) {
      setCurrentIndex(items.length - 2);
    } else if (currentIndex === items.length - 1) {
      setCurrentIndex(1);
    }
  }, [currentIndex, items.length]);

  const handleNext = () => {
    if (!isTransitioning) {
      setIsTransitioning(true);
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isTransitioning) {
      setIsTransitioning(true);
      setCurrentIndex((prev) => prev - 1);
    }
  };

  useEffect(() => {
    const slider = document.querySelector(".carousel-slider");
    if (slider) {
      slider.addEventListener("transitionend", handleTransitionEnd);
    }
    return () => {
      if (slider) {
        slider.removeEventListener("transitionend", handleTransitionEnd);
      }
    };
  }, [handleTransitionEnd]);

  useEffect(() => {
    if (isStopped) return; // Stop auto-scroll if stopped
    const interval = setInterval(() => {
      if (!isHovered.prev && !isHovered.next) {
        handleNext();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [handleNext, isHovered, isStopped]);

  useEffect(() => {
    if (currentIndex > 0 && currentIndex < items.length - 1) {
      setCurrentProperty(propertiesPassed[currentIndex - 1]);
    } else if (currentIndex === 0) {
      setCurrentProperty(propertiesPassed[propertiesPassed.length - 1]);
    } else if (currentIndex === items.length - 1) {
      setCurrentProperty(propertiesPassed[0]);
    }
  }, [currentIndex, propertiesPassed, items.length, setCurrentProperty]);

  return (
    <div style={{ position: "relative" }}>
      {/* Only show navigation and carousel if more than 3 properties */}
      {propertiesPassed.length > 3 && (
        <button
          style={{
            ...styles.nextButton,
            ...(isHovered.next ? styles.buttonHover : {}),
          }}
          onClick={handleNext}
          onMouseEnter={() => setIsHovered({ ...isHovered, next: true })}
          onMouseLeave={() => setIsHovered({ ...isHovered, next: false })}
        >
          <IoMdArrowRoundForward fontSize={25} />
        </button>
      )}

      <div style={styles.carouselContainer}>
        {propertiesPassed.length > 3 ? (
          <div
            className="carousel-slider"
            style={{
              ...styles.innerContainer,
              transform: `translateX(${-currentIndex * CARD_WIDTH}vw)`,
              transition: isTransitioning ? "transform 0.3s ease-in-out" : "none",
            }}
          >
            {items}
          </div>
        ) : (
          // For 3 or fewer properties, render statically without scroll
          <div
            style={{
              ...styles.innerContainer,
              gap: vw(50),
              transition: "none",
              transform: "none",
            }}
          >
            {propertiesPassed.map((property, index) => (
              <RoomDetailsCard
                key={index}
                propertyDetails={property}
                onCardClick={() => handleCardClick(property, index)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Carousel;
